import React, { useState } from 'react';
import { buildImageUrl } from '../../lib/fileServer';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Ripple } from 'primereact/ripple';

/**
 * Componente reutilizable para mostrar la imagen de portada de un producto
 * con todos sus elementos visuales (logos, especificaciones, ofertas, etc.)
 * 
 * @param {Object} producto - Objeto con la información del producto
 * @param {Function} onHeartClick - Función callback para el botón de me gusta (opcional)
 * @param {Object} style - Estilos adicionales para el contenedor (opcional)
 */
const ProductImage = ({
    producto,
    product, // Agregamos soporte para 'product' también
    src,
    alt,
    className,
    onHeartClick = null,
    style = { height: '300px', maxHeight: '300px' }
}) => {
    const [imageError, setImageError] = useState(false);
    const [resolvedSrc, setResolvedSrc] = useState(src ? buildImageUrl(src) : null);

    // Normalizar el objeto producto (puede venir como 'producto' o 'product')
    const productData = producto || product;

    // Si se pasan src y alt directamente (para compatibilidad con usos antiguos)
    if (src && !productData) {
        return (
            <img
                src={resolvedSrc || '/productos/placeholder.png'}
                alt={alt || 'Producto'}
                className={className || "w-full h-full object-contain"}
                style={style}
                onError={() => {
                    if (resolvedSrc !== '/productos/placeholder.png') {
                        setResolvedSrc('/productos/placeholder.png');
                    }
                }}
            />
        );
    }

    // Validación de props
    if (!productData) {
        console.warn('ProductImage: prop "producto" o "product" es requerido');
        return null;
    }

    const handleHeartClick = () => {
        if (onHeartClick && typeof onHeartClick === 'function') {
            onHeartClick(productData);
        }
    };

    // Función para calcular porcentaje de descuento
    const calcularPorcentajeDescuento = (precioLista, precioVenta) => {
        if (!precioLista || !precioVenta) return 0;
        const descuento = ((Number(precioLista) - Number(precioVenta)) / Number(precioLista)) * 100;
        return Math.round(descuento);
    };

    // Normalizar nombres de propiedades (diferentes APIs pueden usar diferentes nombres)
    const fotoUrl = productData.foto_url || productData.imagen_portada || productData.imagen_principal;
    const nombre = productData.nombre || productData.name;
    const precioLista = productData.preciolista || productData.precio_lista;
    const precioVenta = productData.precioventa || productData.precio_venta;

    return (
        <div className="relative bg-white text-center" style={style}>
            {/* Imagen principal del producto */}
            {fotoUrl && !imageError ? (
                <img
                    src={buildImageUrl(fotoUrl)}
                    alt={nombre || 'Producto'}
                    className="h-full object-contain"
                    onError={() => {
                        if (!imageError) {
                            console.warn('Error cargando imagen:', fotoUrl);
                            setImageError(true);
                        }
                    }}
                    style={{ maxWidth: '100%' }}
                />
            ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <img
                        src="/productos/placeholder.png"
                        alt="Imagen no disponible"
                        className="w-full h-full object-contain opacity-50"
                        onError={() => {
                            // Si el placeholder también falla, mostrar solo el ícono
                        }}
                    />
                </div>
            )}

            {/* Botón de me gusta */}
            {/* <Button 
                type="text" 
                className="p-ripple p-link w-3rem h-3rem hover:surface-200 bg-transparent border-circle border- hover:shadow-2 inline-flex align-items-center justify-content-center absolute transition-colors transition-duration-300" 
                style={{ top: '2px', right: '2px' }}
                onClick={handleHeartClick}
            >
                <i className="pi pi-heart text-2xl text-red-500"></i>
                <Ripple />
            </Button> */}

            {/* Contador de me gusta */}
            {/* <div className="flex justify-content-center flex-wrap absolute w-3rem" style={{ top: '2.5rem', right: '2px' }}>
                {productData.megustas && productData.megustas > 0 && (
                    <span className='text-red-500 font-bold' style={{ fontSize: '12px', textShadow: '1px 1px 0 #FFFFFF' }}>
                        {productData.megustas}
                    </span>
                )}
            </div> */}

            {/* Indicadores de oferta */}
            {precioVenta && precioLista && Number(precioVenta) < Number(precioLista) && (
                <>
                    <img src={`/logos/en-oferta.png`} alt="En oferta" height={72} className="absolute" style={{ top: '72px', right: '2px' }} />
                    <Badge
                        value={`Dcto.${calcularPorcentajeDescuento(precioLista, precioVenta)}%`}
                        severity="danger"
                        className='absolute'
                        style={{ top: '130px', right: '2px', backgroundColor: '#a50d00', color: '#fff' }}
                    />
                    {/* <img src={`/navidadlogo.png`} alt="En oferta" height={80} className="absolute" style={{ top: '72px', right: '2px' }} />
                    <Badge 
                        value={`-${calcularPorcentajeDescuento(precioLista, precioVenta)}%`} 
                        severity="danger" 
                        className='absolute' 
                        size="small"
                        style={{ top: '95px', right: '2px', backgroundColor: 'transparent', color: '#ff0000ff' }}
                    /> */}
                </>
            )}

            {/* Logo de marca */}
            {productData.marcalogo && (
                <img
                    src={productData.marcalogo}
                    height={25}
                    className="absolute"
                    style={{ top: '2px', left: '2px' }}
                    alt="Logo marca"
                />
            )}

            {/* Logo de procesador */}
            {productData.procesadorlogo && (
                <img
                    src={productData.procesadorlogo}
                    height={64}
                    className="absolute"
                    style={{ bottom: '2px', left: '2px' }}
                    alt="Logo procesador"
                />
            )}

            {/* Logo de tarjeta gráfica */}
            {productData.tarjetagraficalogo && (
                <img
                    src={productData.tarjetagraficalogo}
                    height={64}
                    className="absolute"
                    style={{ bottom: '70px', left: '2px' }}
                    alt="Logo GPU"
                />
            )}

            {/* Indicador de color */}
            {productData.colorhex && (
                <div
                    className="flex justify-content-center flex-wrap absolute"
                    style={{
                        bottom: '2px',
                        left: '68px',
                        height: '26px',
                        width: '26px',
                        backgroundColor: productData.colorhex,
                        border: '1px solid #ccc',
                        borderRadius: '2px'
                    }}
                    title={`Color: ${productData.color || 'N/A'}`}
                />
            )}

            {/* Información de almacenamiento */}
            {productData.almacenamiento && (
                <div
                    className="flex justify-content-center flex-wrap absolute"
                    style={{ bottom: '2px', right: '2px', height: '44px', width: '44px' }}
                    title={`Almacenamiento: ${productData.almacenamiento}`}
                >
                    <img src={`/logos/ssd.png`} height={26} alt="SSD" />
                    <span className="font-bold text-cyan-700" style={{ fontSize: (productData.almacenamiento?.length <= 3) ? '13px' : '10px', display: 'block', lineHeight: '1' }}>
                        {productData.almacenamiento}
                    </span>
                    <Ripple />
                </div>
            )}

            {/* Información de memoria RAM */}
            {productData.memoria && (
                <div
                    className="absolute"
                    style={{ bottom: '48px', right: '2px', height: '44px', width: '44px', textAlign: 'center' }}
                    title={`Memoria RAM: ${productData.memoria}`}
                >
                    <img src={`/logos/memory-ram.png`} height={26} alt="RAM" style={{ display: 'block', margin: '0 auto 2px auto' }} />
                    <span className="font-bold text-cyan-700" style={{ fontSize: (productData.memoria?.length <= 3) ? '13px' : '10px', display: 'block', lineHeight: '1' }}>
                        {productData.memoria}
                    </span>
                    <Ripple />
                </div>
            )}
        </div>
    );
};

export default ProductImage;
